import { AnimatePresence, motion } from 'framer-motion'
import {
    ArrowLeft,
    CalendarX2,
    ChevronLeft,
    ChevronRight,
    Info,
    Loader2,
    Mail,
    X,
} from 'lucide-react'
import React, { useContext, useEffect, useState } from 'react'
import { FaWhatsapp } from 'react-icons/fa'
import { useLocation, useNavigate } from 'react-router-dom'
import { BASE_URL_API } from '../../../References/URLs'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import AuthContext from '../../../store/auth-context'
import styles from './DRHistory.module.css'

/* ── helpers ─────────────────────────────────────────── */
const PAGE_SIZE = 20

const fmtDate = (dateStr) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    if (isNaN(d)) return dateStr
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}
const fmt12 = (dateStr) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    if (isNaN(d)) return dateStr
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
}
const defaultFrom = () => {
    const d = new Date()
    d.setDate(d.getDate() - 30)
    return d.toISOString().slice(0, 10)
}
const todayStr = () => new Date().toISOString().slice(0, 10)

/* ── chips ───────────────────────────────────────────── */
const STATUS_CHIP = {
    sent:    { label: 'Sent ✓',   cls: 'chipSent' },
    partial: { label: 'Partial',  cls: 'chipPartial' },
    failed:  { label: 'Failed ✗', cls: 'chipFailed' },
}
const TYPE_CHIP = {
    auto:   { label: 'Auto',   cls: 'chipAuto' },
    manual: { label: 'Manual', cls: 'chipManual' },
}

const normalizeTriggerSource = (value) => {
    const normalized = String(value ?? '').toLowerCase()
    if (normalized === 'auto_cron') return 'auto'
    if (normalized === 'manual') return 'manual'
    return normalized
}

/** Derive a display status from a history record */
const deriveStatus = (r) => {
    if (!r.completedAt) return { label: 'Pending', cls: 'chipManual' }
    const sent   = (r.totalEmailsSent ?? 0) + (r.totalWaSent ?? 0)
    const failed = r.totalFailed ?? 0
    if (sent === 0 && failed > 0) return STATUS_CHIP.failed
    if (failed > 0)              return STATUS_CHIP.partial
    return STATUS_CHIP.sent
}
const DETAIL_STATUS = {
    sent:       { label: 'Sent ✓',       cls: 'chipSent' },
    failed:     { label: 'Failed ✗',     cls: 'chipFailed' },
    bounced:    { label: 'Bounced ⚠',    cls: 'chipBounced' },
    complained: { label: 'Marked spam',  cls: 'chipFailed' },
    suppressed: { label: 'Blocked',      cls: 'chipManual' },
}

/* ── Skeleton bar ────────────────────────────────────── */
const SkeletonBar = ({ w = '100%', h = 14 }) => (
    <div className={styles.skeletonBar} style={{ width: w, height: h }} />
)

const DRHistory = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => {
        try { return asCtx?.activeStore && asCtx.activeStore.trim() !== '' ? JSON.parse(asCtx.activeStore) : null }
        catch { return null }
    })()
    const navigate = useNavigate()
    const location = useLocation()

    /* ── filters ── */
    const [dateFrom, setDateFrom] = useState(defaultFrom())
    const [dateTo, setDateTo]     = useState(todayStr())
    const [typeFilter, setTypeFilter] = useState('All') // All | Auto | Manual
    const [page, setPage]         = useState(1)

    /* ── data ── */
    const [records, setRecords]   = useState([])
    const [total, setTotal]       = useState(0)
    const [loading, setLoading]   = useState(true)

    /* ── detail sheet ── */
    const [sheetOpen, setSheetOpen]       = useState(false)
    const [detailRecord, setDetailRecord] = useState(null)
    const [loadingDetail, setLoadingDetail] = useState(false)

    /* ── date picker visibility ── */
    const [showDatePicker, setShowDatePicker] = useState(false)
    const [draftFrom, setDraftFrom] = useState(dateFrom)
    const [draftTo, setDraftTo]     = useState(dateTo)

    /* ── API ── */
    const getHistory = async (p = 1, from = dateFrom, to = dateTo) => {
        setLoading(true)
        try {
            const body = {
                token: authCtx.token,
                storeIdentifier: activeStore?.storeIdentifier,
                dateFrom: from,
                dateTo: to,
                page: p,
                limit: PAGE_SIZE,
            }
            const res = await fetch(BASE_URL_API + 'dailyReportsGetHistory', {
                method: 'POST',
                body: JSON.stringify(body),
            })
            if (!res.ok) return
            const data = await res.json()
            if (data?.status === 'success') {
                setRecords(data?.response?.history ?? [])
                setTotal(data?.response?.pagination?.total ?? 0)
            } else if (data?.message === 'tokenExpired') {
                authCtx.logout()
            }
        } catch { /* silent */ } finally { setLoading(false) }
    }

    const getDetail = async (reportIdentifier) => {
        setLoadingDetail(true)
        setSheetOpen(true)
        setDetailRecord(null)
        try {
            const res = await fetch(BASE_URL_API + 'dailyReportsGetReportDetail', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx.token, storeIdentifier: activeStore?.storeIdentifier, reportIdentifier }),
            })
            if (!res.ok) return
            const data = await res.json()
            if (data?.status === 'success') {
                const report = data?.response?.report ?? {}
                setDetailRecord({
                    ...report,
                    deliveryLog: data?.response?.deliveryLog ?? [],
                })
            } else if (data?.message === 'tokenExpired') authCtx.logout()
        } catch { /* silent */ } finally { setLoadingDetail(false) }
    }

    /* ── close sheet on Escape ── */
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') setSheetOpen(false) }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [])

    /* ── initial load + open report from navigation state ── */
    useEffect(() => {
        getHistory(1)
        if (location?.state?.openReport) {
            getDetail(location.state.openReport)
        }
    }, [activeStore?.storeIdentifier])

    /* ── filter change handlers ── */
    const applyDateRange = () => {
        setDateFrom(draftFrom)
        setDateTo(draftTo)
        setPage(1)
        setShowDatePicker(false)
        getHistory(1, draftFrom, draftTo)
    }
    const changeType = (t) => {
        setTypeFilter(t)
        setPage(1)
        getHistory(1, dateFrom, dateTo)
    }
    const changePage = (p) => {
        setPage(p)
        getHistory(p)
        document.getElementById('dr-history-table')?.scrollIntoView({ behavior: 'smooth' })
    }
    const clearFilters = () => {
        const from = defaultFrom(), to = todayStr()
        setDateFrom(from); setDraftFrom(from)
        setDateTo(to);   setDraftTo(to)
        setTypeFilter('All')
        setPage(1)
        getHistory(1, from, to)
    }

    /* ── pagination ── */
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

    const pageNumbers = () => {
        const pages = []
        for (let i = Math.max(1, page - 1); i <= Math.min(totalPages, page + 1); i++) pages.push(i)
        return pages
    }

    /* ── derived ── */
    const filteredRecords = typeFilter === 'All'
        ? records
        : records.filter(r => normalizeTriggerSource(r.triggerSource) === typeFilter.toLowerCase())
    const hasAnyHistory = total > 0 || filteredRecords.length > 0
    const noResults     = !loading && filteredRecords.length === 0
    const genuinelyEmpty = noResults && total === 0 && typeFilter === 'All'

    return (
        <div className={styles.wrapper}>
            {/* ── Page Header ── */}
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <button className={styles.backBtn} onClick={() => navigate('/reports/daily')} type="button">
                        <ArrowLeft size={14} /> Daily reports
                    </button>
                    <div className={styles.titleRow}>
                        <h1 className={styles.pageTitle}>Report history</h1>
                        {!loading && <span className={styles.totalCount}>{total} reports total</span>}
                    </div>
                </div>
            </div>

            {/* ── Filter bar ── */}
            <div className={styles.filterBar}>
                {/* Date range */}
                <div className={styles.dateRangeWrap}>
                    <button
                        className={styles.dateRangeBtn}
                        onClick={() => setShowDatePicker(v => !v)}
                        type="button"
                    >
                        📅 {dateFrom} → {dateTo}
                    </button>
                    <AnimatePresence>
                        {showDatePicker && (
                            <motion.div
                                className={styles.datePickerPopup}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 6 }}
                                transition={{ duration: 0.15 }}
                            >
                                <div className={styles.datePickerRow}>
                                    <label className={styles.dateLabel}>From</label>
                                    <input
                                        type="date"
                                        className={styles.dateInput}
                                        value={draftFrom}
                                        max={draftTo}
                                        onChange={e => setDraftFrom(e.target.value)}
                                    />
                                </div>
                                <div className={styles.datePickerRow}>
                                    <label className={styles.dateLabel}>To</label>
                                    <input
                                        type="date"
                                        className={styles.dateInput}
                                        value={draftTo}
                                        min={draftFrom}
                                        max={todayStr()}
                                        onChange={e => setDraftTo(e.target.value)}
                                    />
                                </div>
                                <div className={styles.datePickerActions}>
                                    <button className={styles.dateApplyBtn} onClick={applyDateRange} type="button">Apply</button>
                                    <button className={styles.dateCancelBtn} onClick={() => setShowDatePicker(false)} type="button">Cancel</button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Type pills */}
                <div className={styles.typePills}>
                    {['All', 'Auto', 'Manual'].map(t => (
                        <button
                            key={t}
                            className={`${styles.typePill} ${typeFilter === t ? styles.typePillActive : ''}`}
                            onClick={() => changeType(t)}
                            type="button"
                        >
                            {t}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── History Table ── */}
            <div className={styles.tableCard} id="dr-history-table">
                {/* Table header */}
                <div className={styles.tableHeader}>
                    <span>Date</span>
                    <span>Type</span>
                    <span>Delivered to</span>
                    <span>Status</span>
                </div>

                {loading ? (
                    /* Skeleton rows */
                    Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className={styles.tableRowSkeleton}>
                            <div><SkeletonBar w="60%" h={13} /><SkeletonBar w="40%" h={10} /></div>
                            <SkeletonBar w={50} h={20} />
                            <SkeletonBar w={40} h={13} />
                            <SkeletonBar w={56} h={20} />
                        </div>
                    ))
                ) : noResults ? (
                    <div className={styles.emptyState}>
                        <CalendarX2 size={40} color="#cbd5e1" />
                        <p className={styles.emptyTitle}>No reports found</p>
                        <p className={styles.emptySub}>
                            {genuinelyEmpty
                                ? 'No reports sent yet. Use the Send Now button on the dashboard to send your first report.'
                                : 'No reports were sent in the selected date range. Try changing the filters or date range.'}
                        </p>
                        {!genuinelyEmpty && (
                            <button className={styles.clearFiltersBtn} onClick={clearFilters} type="button">
                                Clear filters
                            </button>
                        )}
                    </div>
                ) : (
                    filteredRecords.map((r, i) => {
                        const status    = deriveStatus(r)
                        const triggerType = normalizeTriggerSource(r.triggerSource)
                        const type        = TYPE_CHIP[triggerType] ?? { label: r.triggerSource, cls: 'chipManual' }
                        const sentCount = (r.totalEmailsSent ?? 0) + (r.totalWaSent ?? 0)
                        const channels  = (r.sentOnEmail ?? 0) + (r.sentOnWhatsApp ?? 0)
                        const delivered = channels > 0 ? `${sentCount} of ${channels} ch.` : `${sentCount}`
                        return (
                            <div
                                key={r.reportIdentifier ?? i}
                                className={styles.tableRow}
                                onClick={() => getDetail(r.reportIdentifier)}
                            >
                                <div className={styles.dateCell}>
                                    <span className={styles.datePrimary}>{fmtDate(r.reportDate ?? r.createdOn)}</span>
                                    <span className={styles.dateSub}>{fmt12(r.createdOn)}</span>
                                </div>
                                <span className={`${styles.chip} ${styles[type.cls]}`}>{type.label}</span>
                                <span className={styles.deliveredCell}>{delivered}</span>
                                <div className={styles.statusCell}>
                                    <span className={`${styles.chip} ${styles[status.cls]}`}>{status.label}</span>
                                    <ChevronRight size={14} color="#cbd5e1" />
                                </div>
                            </div>
                        )
                    })
                )}
            </div>

            {/* ── Pagination ── */}
            {!loading && totalPages > 1 && (
                <div className={styles.pagination}>
                    <button
                        className={styles.pageBtn}
                        onClick={() => changePage(page - 1)}
                        disabled={page === 1}
                        type="button"
                    >
                        <ChevronLeft size={15} /> Previous
                    </button>
                    {pageNumbers().map(p => (
                        <button
                            key={p}
                            className={`${styles.pageNumBtn} ${p === page ? styles.pageNumBtnActive : ''}`}
                            onClick={() => changePage(p)}
                            type="button"
                        >
                            {p}
                        </button>
                    ))}
                    <button
                        className={styles.pageBtn}
                        onClick={() => changePage(page + 1)}
                        disabled={page === totalPages}
                        type="button"
                    >
                        Next <ChevronRight size={15} />
                    </button>
                </div>
            )}

            {/* ── Report Detail Sheet ── */}
            <AnimatePresence>
                {sheetOpen && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            className={styles.sheetBackdrop}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSheetOpen(false)}
                        />
                        {/* Sheet */}
                        <motion.div
                            className={styles.sheet}
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', stiffness: 320, damping: 36 }}
                        >
                            {loadingDetail ? (
                                <div className={styles.sheetLoading}>
                                    {/* Header skeleton */}
                                    <div className={styles.sheetHeaderSkeleton}>
                                        <SkeletonBar w="50%" h={18} />
                                        <SkeletonBar w="70%" h={12} />
                                    </div>
                                    <div className={styles.sheetDivider} />
                                    <div className={styles.sheetBody}>
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className={styles.recipientRowSkeleton}>
                                                <SkeletonBar w={32} h={32} />
                                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                                                    <SkeletonBar w="65%" h={13} />
                                                    <SkeletonBar w="45%" h={10} />
                                                </div>
                                                <SkeletonBar w={60} h={22} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : detailRecord ? (
                                <>
                                    {/* Sheet header */}
                                    <div className={styles.sheetHeader}>
                                        <div className={styles.sheetHeaderLeft}>
                                            <div className={styles.sheetDate}>{fmtDate(detailRecord.reportDate ?? detailRecord.createdOn)}</div>
                                            <div className={styles.sheetMeta}>
                                                <span className={`${styles.chip} ${styles[TYPE_CHIP[normalizeTriggerSource(detailRecord.triggerSource)]?.cls ?? 'chipManual']}`}>
                                                    {TYPE_CHIP[normalizeTriggerSource(detailRecord.triggerSource)]?.label ?? detailRecord.triggerSource}
                                                </span>
                                                <span className={styles.sheetMetaText}>
                                                    Created {fmt12(detailRecord.createdOn)}
                                                    {detailRecord.completedAt ? ` · Completed ${fmt12(detailRecord.completedAt)}` : ' · Pending'}
                                                    {' · '}{(detailRecord.deliveryLog ?? []).filter(e => e.status === 'sent').length}/{(detailRecord.deliveryLog ?? []).length} reached
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            className={styles.sheetCloseBtn}
                                            onClick={() => setSheetOpen(false)}
                                            type="button"
                                            aria-label="Close"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>

                                    <div className={styles.sheetDivider} />

                                    {/* Delivery breakdown */}
                                    <div className={styles.sheetBody}>
                                        <div className={styles.sheetSectionLabel}>Delivery breakdown</div>
                                        <div className={styles.recipientList}>
                                            {(detailRecord.deliveryLog ?? []).map((entry, i) => {
                                                const isWA = entry.channel === 'whatsapp'
                                                const chipInfo = DETAIL_STATUS[entry.status] ?? { label: entry.status, cls: 'chipManual' }
                                                return (
                                                    <div key={i} className={styles.recipientRow}>
                                                        <div className={styles.recipientIcon}>
                                                            {isWA
                                                                ? <FaWhatsapp size={15} color="#25D366" />
                                                                : <Mail size={15} color="#2463EB" />}
                                                        </div>
                                                        <div className={styles.recipientInfo}>
                                                            <span className={styles.recipientAddr}>{entry.recipient}</span>
                                                            <span className={styles.recipientMeta}>
                                                                {entry.recipientRole}{isWA ? ' · WhatsApp' : ''} · {fmt12(entry.attemptedAt ?? detailRecord.createdOn)}
                                                            </span>
                                                        </div>
                                                        <span className={`${styles.chip} ${styles[chipInfo.cls]}`}>
                                                            {chipInfo.label}
                                                        </span>
                                                    </div>
                                                )
                                            })}
                                        </div>

                                        {/* Info banner if any failure */}
                                        {(detailRecord.deliveryLog ?? []).some(e => e.status !== 'sent') && (
                                            <div className={styles.sheetInfoBanner}>
                                                <Info size={15} color="#64748b" className={styles.sheetInfoIcon} />
                                                <p>One or more recipients could not be reached. Check your receivers list to ensure addresses are correct and active.</p>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className={styles.sheetError}>
                                    <p>Could not load report details. Please try again.</p>
                                    <button onClick={() => setSheetOpen(false)} className={styles.clearFiltersBtn} type="button">Close</button>
                                </div>
                            )}
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    )
}

export default DRHistory
